<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { getStore } from '@core/store';

const store = getStore('appData');
const operations = ref([]);
const selectedCountry = ref('');
const expandedOperations = ref({});
const editorRef = ref(null);

const countries = {
  BJ: { name: 'Bénin', flag: '🇧🇯' },
  BF: { name: 'Burkina Faso', flag: '🇧🇫' },
  CI: { name: 'Côte d\'Ivoire', flag: '🇨🇮' },
  GA: { name: 'Gabon', flag: '🇬🇦' },
  ML: { name: 'Mali', flag: '🇲🇱' },
  NE: { name: 'Niger', flag: '🇳🇪' },
  SN: { name: 'Sénégal', flag: '🇸🇳' },
  TG: { name: 'Togo', flag: '🇹🇬' }
};

const operators = {
  MTN: { name: 'MTN' },
  MOOV: { name: 'Moov' },
  AIRTEL: { name: 'Airtel' },
  NITA: { name: 'Nita' }
};

onMounted(() => {
  loadOperations();
  watchStore();
});

function watchStore() {
  const checkStore = () => {
    if (store?.state?.data?.manualOperationsInstructions) {
      operations.value = store.state.data.manualOperationsInstructions || [];
    }
  };
  checkStore();
  const interval = setInterval(checkStore, 1000);
  onUnmounted(() => clearInterval(interval));
}

function loadOperations() {
  operations.value = store?.state?.data?.manualOperationsInstructions || [];
}

const filteredOperations = computed(() => {
  if (!selectedCountry.value) return operations.value;
  return operations.value.filter(op => op.country === selectedCountry.value);
});

const groupedOperations = computed(() => {
  const grouped = {};
  filteredOperations.value.forEach(op => {
    const key = `${op.country}-${op.operator}`;
    if (!grouped[key]) {
      grouped[key] = op;
    }
  });
  return grouped;
});

const countryCodes = computed(() => {
  const codes = new Set();
  operations.value.forEach(op => codes.add(op.country));
  return Array.from(codes).sort();
});

function toggleExpand(key) {
  expandedOperations.value[key] = !expandedOperations.value[key];
}

function addNew() {
  editorRef.value?.open(null);
}

function editOperation(operation) {
  editorRef.value?.open(operation);
}

function copyOperation(operation) {
  const copy = JSON.parse(JSON.stringify(operation));
  editorRef.value?.open(copy);
}

async function deleteOperation(operation) {
  const result = await window.customPrompt({
    title: 'Supprimer l\'opération',
    message: `Êtes-vous sûr de vouloir supprimer l\'opération ${operators[operation.operator]?.name} pour ${countries[operation.country]?.name} ?`,
    successText: 'Supprimer',
    cancelText: 'Annuler',
    withInput: false
  });

  if (result?.accepted) {
    const index = operations.value.findIndex(op =>
      op.country === operation.country && op.operator === operation.operator
    );
    if (index !== -1) {
      operations.value.splice(index, 1);
      saveOperations();
    }
  }
}

function saveOperations() {
  window.$bus.emit(window.$constants.EVENTS.APP_DATA.UPDATE_MANUAL_OPERATIONS, {
    manualOperationsInstructions: operations.value
  });
}

function handleSave(operationData) {
  const existingIndex = operations.value.findIndex(
    op => op.country === operationData.country &&
    op.operator === operationData.operator
  );

  if (existingIndex !== -1) {
    operations.value[existingIndex] = operationData;
  } else {
    operations.value.push(operationData);
  }
  saveOperations();
}
</script>

<template>
  <div class="space-y-6">
    <!-- HEADER -->
    <div class="flex justify-between items-center">
      <h3 class="text-lg font-semibold text-gray-800">
        Opérations manuelles
      </h3>
      <button
        class="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 font-medium"
        @click="addNew"
      >
        + Ajouter
      </button>
    </div>

    <!-- EMPTY STATE -->
    <div v-if="operations.length === 0" class="bg-white rounded-xl p-8 text-center">
      <p class="text-gray-500">Aucune opération configurée</p>
      <button
        class="mt-4 px-6 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        @click="addNew"
      >
        Ajouter une opération
      </button>
    </div>

    <!-- FILTERS -->
    <div v-else class="space-y-4">
      <!-- COUNTRY FILTER -->
      <div>
        <label class="block mb-2 text-sm font-medium text-gray-700">Pays</label>
        <div class="flex flex-wrap gap-2">
          <button
            @click="selectedCountry = ''"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all',
              selectedCountry === ''
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            ]"
          >
            Tous
          </button>
          <button
            v-for="code in countryCodes"
            :key="code"
            @click="selectedCountry = code"
            :class="[
              'px-4 py-2 rounded-lg font-medium transition-all',
              selectedCountry === code
                ? 'bg-primary-600 text-white'
                : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
            ]"
          >
            {{ countries[code]?.flag }} {{ countries[code]?.name }}
          </button>
        </div>
      </div>

      <!-- OPERATIONS LIST -->
      <div class="space-y-3">
        <div
          v-for="(operation, key) in groupedOperations"
          :key="key"
          class="bg-white rounded-xl shadow-sm overflow-hidden"
        >
          <!-- HEADER -->
          <div
            @click="toggleExpand(key)"
            class="flex justify-between items-center p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          >
            <div class="flex items-center gap-3">
              <span class="text-2xl">{{ countries[operation.country]?.flag }}</span>
              <span class="font-semibold">{{ operators[operation.operator]?.name }}</span>
              <span class="text-sm text-gray-500">
                {{ operation.instructions?.length || 0 }} étape(s)
              </span>
            </div>
            <div class="flex items-center gap-2">
              <button
                class="px-3 py-1 text-sm rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200"
                @click.stop="editOperation(operation)"
              >
                Modifier
              </button>
              <button
                class="px-3 py-1 text-sm rounded-lg bg-blue-100 text-blue-700 hover:bg-blue-200"
                @click.stop="copyOperation(operation)"
              >
                Copier
              </button>
              <button
                class="px-3 py-1 text-sm rounded-lg bg-red-100 text-red-700 hover:bg-red-200"
                @click.stop="deleteOperation(operation)"
              >
                Supprimer
              </button>
              <svg
                class="w-5 h-5 text-gray-400 transition-transform"
                :class="{ 'rotate-180': expandedOperations[key] }"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>

          <!-- DETAILS -->
          <div v-show="expandedOperations[key]" class="p-4 space-y-4">
            <div v-if="operation.ussdCode" class="text-sm text-gray-600">
              <span class="font-medium">Code USSD:</span> {{ operation.ussdCode }}
            </div>

            <!-- INSTRUCTIONS -->
            <div v-if="operation.instructions?.length > 0" class="space-y-3">
              <h4 class="font-medium text-gray-900">Instructions</h4>
              <div class="space-y-2">
                <div
                  v-for="(instruction, idx) in operation.instructions"
                  :key="idx"
                  class="bg-gray-50 rounded-lg p-3 border border-gray-200"
                >
                  <div class="text-sm" v-html="instruction.text"></div>
                </div>
              </div>
            </div>

            <!-- REQUIRED FIELDS -->
            <div v-if="operation.requiredFields?.length > 0">
              <h4 class="font-medium text-gray-900 mb-2">Champs requis</h4>
              <div class="flex flex-wrap gap-2">
                <span
                  v-for="(field, idx) in operation.requiredFields"
                  :key="idx"
                  class="px-3 py-1 bg-gray-100 text-gray-700 rounded-lg text-sm"
                >
                  {{ field.label }}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- EDITOR MODAL -->
    <ManualOperationEditor
      ref="editorRef"
      @save="handleSave"
    />
  </div>
</template>
